import { useState, useEffect, useMemo } from "react";
import useSWR from "swr";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { fetcher } from "../../../../services/fetcher";
import { Select, Center, Paper, SimpleGrid, Button, NumberInput, Radio, Group, Divider, TextInput } from "@mantine/core";
import dayjs from "dayjs";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import nationalities from "../../../../data/nationality.json";
import countries from "../../../../data/countries.json";
import { errorNotifications, successNotifications } from "../../../../utils/handleNotifications";
import { normalizeCoverFilterQuote } from "../../../issuance/utils/normalizeCoverFilterQuote";

const COVER_ACCESS_URL = "issuance/user-cover-plan-access";

const GetYourQuoteBooking = ({
  onUrlReady,
  selectedOperator,
  embedded = false,
  coverFilterMode = false,
  issueAs = "User",
  onTripPreview,
}) => {
  const userRole = useSelector((state) => state.userProfile?.user?.role);
  const authRole = useSelector((state) => state.auth.role);

  const { handleSubmit, control, watch, register, setValue } = useForm({
    defaultValues: {
      travel_dates: [new Date(), new Date()],
      nationality: "Indian",
      travelling_from: "India",
      travelling_to: "India",
      cover_id: "",
    },
  });
  const [value, setValueRange] = useState([new Date(), new Date()]);
  const [numPax, setNumPax] = useState(1);
  const [isQuoting, setIsQuoting] = useState(false);
  // const opValue = operator ? operator.id : selectedOperator;

  const nationality = watch("nationality");
  const travellingTo = watch("travelling_to");
  const travellingFrom = watch("travelling_from");
  const adventure = watch("adventure");
  const residence_status = watch("residence_status");
  const travelDates = watch("travel_dates");

  const journeyDuration = useMemo(() => {
    const start = travelDates?.[0];
    const end = travelDates?.[1];
    if (!start || !end) return null;

    const startDay = dayjs(start).startOf("day");
    const endDay = dayjs(end).startOf("day");
    if (!startDay.isValid() || !endDay.isValid()) return null;

    const days = endDay.diff(startDay, "day") + 1;
    return days > 0 ? days : null;
  }, [travelDates]);

  useEffect(() => {
    if (!embedded || !onTripPreview) return;

    onTripPreview({
      travelling_from: travellingFrom,
      travelling_to: travellingTo,
      start_date: travelDates?.[0]
        ? dayjs(travelDates[0]).format("YYYY-MM-DD")
        : null,
      end_date: travelDates?.[1]
        ? dayjs(travelDates[1]).format("YYYY-MM-DD")
        : null,
      duration:
        journeyDuration != null
          ? `${journeyDuration} day${journeyDuration === 1 ? "" : "s"}`
          : null,
      travelers: numPax,
    });
  }, [
    embedded,
    onTripPreview,
    travellingFrom,
    travellingTo,
    travelDates,
    journeyDuration,
    numPax,
  ]);

  const allRequiredParamsAvailable =
    nationality && travellingTo && travellingFrom;

  const plansUrl = useMemo(() => {
    if (!coverFilterMode) return null;
    const roles = Array.isArray(authRole) ? authRole : [authRole];
    if (roles.some((r) => String(r).includes("Operator"))) {
      return `${COVER_ACCESS_URL}/?policy_type=bulk`;
    }
    if (!userRole) return null;
    if (
      issueAs === "User" &&
      ["Issuance Team", "Sales Team", "Super Admin"].includes(userRole)
    ) {
      return `${COVER_ACCESS_URL}/?policy_type=single&issue_as=agent`;
    }
    if (issueAs === "Operator" && selectedOperator) {
      return `${COVER_ACCESS_URL}/?policy_type=single&issue_as=operator&operator=${selectedOperator}`;
    }
    if (issueAs === "Operator") {
      return `${COVER_ACCESS_URL}/?policy_type=single&issue_as=operator`;
    }
    return null;
  }, [coverFilterMode, issueAs, selectedOperator, authRole, userRole]);

  const { data: plansData } = useSWR(plansUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const planOptions = useMemo(
    () =>
      plansData?.covers?.flatMap((cover) =>
        cover.customize_covers?.map((item) => ({
          value: String(item.id),
          label: item.title,
        })) || []
      ) || [],
    [plansData]
  );

  const { data: adventureOptions } = useSWR(
    !coverFilterMode && allRequiredParamsAvailable
      ? `quotation/adventure-options/?${new URLSearchParams({
          nationality,
          travelling_to: travellingTo,
          travelling_from: travellingFrom,
          ...(residence_status &&
           residence_status === "yes" && { residence_status }),
          ...(selectedOperator && { operator: selectedOperator }), // Pass the number directly
        }).toString()}`
      : null,
    fetcher
  );

  const transformOptions = () => {
    if (!adventureOptions) return [];

    return Object.keys(adventureOptions).map((key) => ({
      group: key,
      items: adventureOptions[key],
    }));
  };

  const adventureOptionsList = adventureOptions && transformOptions();

  useEffect(() => {
    register("no_of_pax", { value: numPax });
  }, [numPax, register]);

  useEffect(() => {
    if (coverFilterMode && planOptions.length === 1) {
      setValue("cover_id", planOptions[0].value);
    }
  }, [coverFilterMode, planOptions, setValue]);

  useEffect(() => {
    if (
      !coverFilterMode &&
      adventureOptionsList?.length === 1 &&
      adventureOptionsList[0].items.length === 1
    ) {
      setValue("adventure", adventureOptionsList[0].items[0]);
    }
  }, [coverFilterMode, adventureOptionsList, setValue]);

  const onSubmit = async (data) => {
  const start_date = data.travel_dates?.[0]
    ? dayjs(data.travel_dates[0]).format("YYYY-MM-DD")
    : null;
  const end_date = data.travel_dates?.[1]
    ? dayjs(data.travel_dates[1]).format("YYYY-MM-DD")
    : null;

  const age_fields = [];
  for (let i = 1; i <= numPax; i++) {
    age_fields.push(data[`age_${i}`]);
  }

  if (coverFilterMode) {
    const coverId = Number(data.cover_id);
    if (!data.cover_id || Number.isNaN(coverId)) {
      errorNotifications("Please select a plan.");
      return;
    }
    setIsQuoting(true);
    try {
      const res = await fetcher("crm/covers/filter/", "POST", {
        cover_id: coverId,
        start_date,
        end_date,
        ages: age_fields,
        nationality: data.nationality,
        travelling_from: travellingFrom,
        travelling_to: travellingTo,
      });
      const quote = normalizeCoverFilterQuote(res, {
        cover_id: coverId,
        nationality: data.nationality,
        travelling_from: travellingFrom,
        travelling_to: travellingTo,
        start_date,
        end_date,
        ages: age_fields,
        residence_status: data.residence_status,
      });
      if (!quote) {
        errorNotifications("No pricing found for the selected plan.");
        return;
      }
      successNotifications("Quote fetched successfully");
      onUrlReady(null, quote);
    } catch (error) {
      errorNotifications(error.response?.data || error, error);
    } finally {
      setIsQuoting(false);
    }
    return;
  }

  const queryParams = new URLSearchParams({
    start_date,
    end_date,
    ...age_fields.reduce(
      (acc, age, index) => ({ ...acc, [`age_fields[${index}]`]: age }),
      {}
    ),
    adventure,
    nationality,
    travelling_to: travellingTo,
    travelling_from: travellingFrom,
    residence_status: data.residence_status,
  }).toString();

  const url = `quotation/cover-pricing/?${queryParams}`;

  try {
    const response = await fetcher(url); 

    // Handle message in response
    if (response?.message) {
      notifications.show({
        title: "Notice",
        message: response.message,
        color: "yellow", 
      });
    } else {
      successNotifications("Quote fetched successfully");
    }

    // Optionally pass response to parent
    onUrlReady(url, response);
  } catch (error) {
      if (error?.response?.data) {
        errorNotifications(error.response.data, error);
      } else {
        errorNotifications({}, error);
      }
    }
  };

  const showResidenceStatus = useMemo(() => {
    return nationality !== "Indian" && travellingFrom === "India";
  }, [nationality, travellingFrom]);

  const gridSpacing = embedded
    ? { spacing: "md", verticalSpacing: "sm" }
    : { spacing: { base: 10, sm: "xl" }, verticalSpacing: { base: "md", sm: "xl" } };
  const actionSpacing = embedded ? "md" : "xl";

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 2 }} align="flex-start" {...gridSpacing}>
            <Controller
              name="nationality"
              control={control}
              render={({ field }) => (
                <Select
                  label="Nationality"
                  placeholder="Select user nationality"
                  data={nationalities.map((item) => item.name)}
                  searchable
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="travelling_from"
              control={control}
              render={({ field }) => (
                <Select
                  label="Travelling From"
                  placeholder="Select travelling from"
                  data={countries.map((item) => item.name)}
                  searchable
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="travelling_to"
              control={control}
              render={({ field }) => (
                <Select
                  label="Travelling To"
                  placeholder="Select travelling to"
                  data={countries.map((item) => item.name)}
                  searchable
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            {/* Indian Resident Status */}
            {showResidenceStatus && (
              <Controller
                name="residence_status"
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    {...field}
                    name="residence_status"
                    id="residenceStatus"
                    label="Are all travelers Indian residents with OCI, PAN, or an Indian bank account?"
                    withAsterisk
                  >
                    <Group mt="xs">
                      <Radio value="yes" label="Yes" />
                      <Radio value="no" label="No" />
                    </Group>
                  </Radio.Group>
                )}
              />
            )}

            <Controller
              name="travel_dates"
              control={control}
              render={({ field }) => (
                <DatePickerInput
                  type="range"
                  label="Journey Dates"
                  placeholder="Journey Date - End Date"
                  value={field.value || value}
                  minDate={new Date()}
                  onChange={(val) => {
                    setValueRange(val);
                    field.onChange(val);
                  }}
                  allowSingleDateInRange
                />
              )}
            />

            <TextInput
              label="Duration"
              value={
                journeyDuration != null
                  ? `${journeyDuration} day${journeyDuration === 1 ? "" : "s"}`
                  : ""
              }
              placeholder="Auto-calculated"
              readOnly
            />

            {coverFilterMode ? (
              <Controller
                name="cover_id"
                control={control}
                rules={{ required: "Plan is required" }}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    label="Plan"
                    placeholder="Select a plan"
                    data={planOptions}
                    searchable
                    withAsterisk
                    value={field.value}
                    onChange={field.onChange}
                    error={error?.message}
                    disabled={planOptions.length === 1}
                  />
                )}
              />
            ) : (
              <Controller
                name="adventure"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Adventure"
                    placeholder="Select an adventure"
                    data={adventureOptionsList}
                    searchable
                    disabled={
                      adventureOptionsList?.length === 1 &&
                      adventureOptionsList[0].items.length === 1
                    }
                    {...field}
                  />
                )}
              />
            )}

            <Controller
              name="no_of_pax"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="No. of pax"
                  placeholder="Enter no.of traveller"
                  min={1}
                  max={10}
                  allowNegative={false}
                  allowDecimal={false}
                  hideControls
                  value={field.value}
                  defaultValue={1}
                  onChange={(value) => {
                    setNumPax(value);
                    field.onChange(value);
                  }}
                />
              )}
            />

            {Array.from({ length: numPax }, (_, index) => (
              <Controller
                key={index}
                name={`age_${index + 1}`}
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label={`Age of person ${index + 1}`}
                    placeholder={`Enter age of person ${index + 1}`}
                    min={1}
                    max={80}
                    allowNegative={false}
                    allowDecimal={false}
                    hideControls
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              />
            ))}
          </SimpleGrid>
          <Divider mt={actionSpacing} />
          <Center>
            <Button type="submit" variant="gradient" mt={actionSpacing} px={28} py={8} loading={isQuoting}>
              Get Quote
            </Button>
          </Center>
        </form>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <>
      <Paper p="xl" my="lg">
        {formContent}
      </Paper>
    </>
  );
};

export default GetYourQuoteBooking;